import { all, takeLatest, call, put } from 'redux-saga/effects';
import { ActionType } from 'deox';

import env, { SiteId } from '@/env';
import { navigate } from '@reach/router';
import { camelCase, executeAfterToast } from '@/utils';
import { OptionFilter, ApiCategory, AppCategory, EntryType } from '@/types';
import { CategoryInstance } from '@/services/service-directory/category';
import {
  fetchCategoriesActions,
  fetchCategoryByIdActions,
  createCategoryActions,
  editCategoryActions,
  deleteCategoryActions,
} from '@/redux/actions/service-directory/category';

export function* fetchCategoriesSaga(action: ActionType<typeof fetchCategoriesActions.request>) {
  const { entryType, industry } = action.payload;
  try {
    const categories = yield call(CategoryInstance.fetchCategories, entryType, industry);
    const filterOptions: { [checkbox: string]: OptionFilter } = {};
    categories.forEach((category: ApiCategory | AppCategory) => {
      filterOptions[camelCase(category.name)] = {
        label: category.name,
        dotColor: undefined,
        checked: false,
        value: category.name,
      };
    });
    yield put(fetchCategoriesActions.success(entryType, filterOptions, categories));
  } catch (err) {
    yield put(fetchCategoriesActions.failure(err));
  }
}

export function* fetchCategorySaga(action: ActionType<typeof fetchCategoryByIdActions.request>) {
  try {
    const { id } = action.payload;
    const category = yield call(CategoryInstance.fetchCategory, id);
    yield put(fetchCategoryByIdActions.success(category));
  } catch (err) {
    yield put(fetchCategoryByIdActions.failure(err));
  }
}

export function* createCategorySaga(action: ActionType<typeof createCategoryActions.request>) {
  try {
    const { categoryInfo } = action.payload;
    yield call(CategoryInstance.createCategory, categoryInfo);
    yield put(createCategoryActions.success());

    // refetch categories
    yield put(fetchCategoriesActions.request(categoryInfo.entryType));

    if (env.siteId === SiteId.Directory) {
      const currentDashboardProduct = window.location.pathname.split('/')[1];
      executeAfterToast(() =>
        navigate(
          `/${currentDashboardProduct}/dashboard/${
            categoryInfo.entryType === EntryType.API ? 'api-categories' : 'app-categories'
          }`
        )
      );
    } else {
      executeAfterToast(() =>
        navigate(`/dashboard/${categoryInfo.entryType === EntryType.API ? 'api-categories' : 'app-categories'}`)
      );
    }
  } catch (err) {
    yield put(createCategoryActions.failure(err));
  }
}

export function* editCategorySaga(action: ActionType<typeof editCategoryActions.request>) {
  try {
    const { id, categoryInfo } = action.payload;

    yield call(CategoryInstance.updateCategory, id, categoryInfo);
    yield put(editCategoryActions.success());

    // refetch categories
    yield put(fetchCategoriesActions.request(categoryInfo.entryType));

    if (env.siteId === SiteId.Directory) {
      const currentDashboardProduct = window.location.pathname.split('/')[1];
      executeAfterToast(() =>
        navigate(
          `/${currentDashboardProduct}/dashboard/${
            categoryInfo.entryType === EntryType.API ? 'api-categories' : 'app-categories'
          }`
        )
      );
    } else {
      executeAfterToast(() =>
        navigate(`/dashboard/${categoryInfo.entryType === EntryType.API ? 'api-categories' : 'app-categories'}`)
      );
    }
  } catch (err) {
    yield put(editCategoryActions.failure(err));
  }
}

export function* deleteCactegorySaga(action: ActionType<typeof deleteCategoryActions.request>) {
  try {
    const { id, entryType } = action.payload;

    yield call(CategoryInstance.deleteCategory, id);
    yield put(deleteCategoryActions.success());

    // refetch categories
    yield put(fetchCategoriesActions.request(entryType));

    if (env.siteId === SiteId.Directory) {
      const currentDashboardProduct = window.location.pathname.split('/')[1];
      executeAfterToast(() =>
        navigate(
          `/${currentDashboardProduct}/dashboard/${entryType === EntryType.API ? 'api-categories' : 'app-categories'}`
        )
      );
    } else {
      executeAfterToast(() =>
        navigate(`/dashboard/${entryType === EntryType.API ? 'api-categories' : 'app-categories'}`)
      );
    }
  } catch (err) {
    yield put(deleteCategoryActions.failure(err));
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchCategoriesActions.request.type, fetchCategoriesSaga),
    takeLatest(fetchCategoryByIdActions.request.type, fetchCategorySaga),
    takeLatest(createCategoryActions.request.type, createCategorySaga),
    takeLatest(editCategoryActions.request.type, editCategorySaga),
    takeLatest(deleteCategoryActions.request.type, deleteCactegorySaga),
  ]);
}
